import { toSnakeCase } from '@/helpers/string-helpers'
import { BookingDraft } from '@/scopes/checkout/domain/types'
import {
  AvailableRoomType,
  NewRoomBeingCreated,
  Room,
  RoomSelectionTypeRadioGroupOptions,
} from '@/scopes/checkout/groups/domain/types/availableRooms'

export function isAnyRoomAlreadyNamed(rooms: Record<string, Room> | null | undefined): boolean {
  if (!rooms)
    return false

  return Object.values(rooms).some(({ name }) => !!name)
}

export function isAnyRoomTypeAvailable(roomTypes: Record<string, AvailableRoomType> | null | undefined): boolean {
  if (!roomTypes)
    return false

  return Object.values(roomTypes).some(({ availableRoomsAmount }) => availableRoomsAmount > 0)
}

export function getSelectedRoom(rooms: Record<string, Room> | null | undefined, bookingDraftData: BookingDraft | undefined): Room | null {
  if (!bookingDraftData)
    return null

  const { groupInformation } = bookingDraftData

  if (!groupInformation || !rooms)
    return null

  const selectedRoomName = groupInformation.selectedRoom?.name
  if (!selectedRoomName)
    return null

  const selectedRoom = Object.values(rooms).find(({ name }) =>
    toSnakeCase(name) === toSnakeCase(selectedRoomName),
  )

  return selectedRoom ?? null
}

export function isAnyNamedRoomWithSameName(rooms: Record<string, Room> | null | undefined, roomName: string): boolean {
  if (!rooms)
    return false

  return Object.values(rooms).some(room => toSnakeCase(room.name) === toSnakeCase(roomName))
}

export function getMethodAllocationFromSelectedRoom(bookingDraftData: BookingDraft | undefined): RoomSelectionTypeRadioGroupOptions | null {
  if (!bookingDraftData)
    return null

  const { groupInformation } = bookingDraftData

  if (!groupInformation)
    return null

  return groupInformation.selectedRoom?.method ?? null
}

export function getPinCodeFromSelectedRoom(bookingDraftData: BookingDraft | undefined): string | null {
  if (!bookingDraftData)
    return null

  const { groupInformation } = bookingDraftData

  if (!groupInformation)
    return null

  return groupInformation.selectedRoom?.pinCode ?? null
}

export function isRandomAllocationSelected(bookingDraftData: BookingDraft | undefined): boolean {
  if (!bookingDraftData)
    return false

  const { groupInformation } = bookingDraftData

  if (!groupInformation)
    return false

  return groupInformation.selectedRoom?.method === 'random'
}

export function isNewRoomBeingCreated(bookingDraftData: BookingDraft | undefined): boolean {
  if (!bookingDraftData)
    return false

  const { groupInformation } = bookingDraftData

  if (!groupInformation)
    return false

  return !!groupInformation.selectedRoom?.isNewRoom
}

export function getNewRoomBeingCreated(roomTypes: Record<string, AvailableRoomType> | null | undefined, bookingDraftData: BookingDraft | undefined): NewRoomBeingCreated | null {
  if (!bookingDraftData)
    return null

  const { groupInformation } = bookingDraftData

  if (!groupInformation || !groupInformation.selectedRoom?.isNewRoom)
    return null

  if (!roomTypes)
    return null

  const type = groupInformation.selectedRoom?.type || ''

  const roomType = roomTypes[type]
  return {
    name: groupInformation.selectedRoom.name || '',
    description: roomType?.description || '',
    availableBedsAmount: roomType?.totalBedsAmount - 1 || 0,
    totalBedsAmount: roomType?.totalBedsAmount || 0,
    type,
  }
}

export function isRoomSelected(bookingDraftData: BookingDraft | undefined): boolean {
  if (!bookingDraftData)
    return false

  const { groupInformation } = bookingDraftData

  if (!groupInformation)
    return false

  return !!groupInformation.selectedRoom
}
